import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { cartApi, type CartResponse } from '@/modules/order/api/cart.api'
import { useAuthSession } from '@/modules/auth/public'
import { parsePrice } from '@/utils/price'

export interface CartItem {
  productId: string | number
  name: string
  price: number
  priceWithTax?: number
  taxRate?: number
  taxAmount?: number
  totalPrice?: number
  sku?: string | null
  image?: string
  quantity: number
  minimumOrder?: number
}

interface CartTotals {
  totalAmount: number
  taxAmount: number
}

/**
 * Чем закончилась замена корзины набором из черновика.
 *
 * `replaced` - корзина содержит позиции черновика;
 * `rolled_back` - черновик не восстановился, прежняя корзина возвращена целиком;
 * `inconsistent` - не удалось ни восстановить, ни откатить; содержимое корзины
 * достоверно неизвестно и требует проверки пользователем.
 */
export type CartRestoreOutcome = 'replaced' | 'rolled_back' | 'inconsistent'

export interface CartRestoreResult {
  outcome: CartRestoreOutcome
  failedItems: CartItem[]
}

interface CartInitResult {
  syncedCount: number
  failedItems: CartItem[]
  warnings: string[]
}

export const useCartStore = defineStore('cart', () => {
  // Состояние
  const items = ref<CartItem[]>([])
  const loading = ref(false)
  const supplier = ref<{ id: number; name: string } | null>(null)
  const totals = ref<CartTotals>({
    totalAmount: 0,
    taxAmount: 0,
  })
  const authSession = useAuthSession()

  // Геттеры
  const itemsCount = computed(() => items.value.reduce((sum, item) => sum + item.quantity, 0))

  const totalAmount = computed(() => totals.value.totalAmount)
  const taxAmount = computed(() => totals.value.taxAmount)

  const isEmpty = computed(() => items.value.length === 0)

  // Действия
  async function addItem(item: CartItem) {
    if (shouldUseServerCart()) {
      const response = await cartApi.addItem(item.productId, item.quantity)
      applyCartResponse(response)
      return
    }

    const existingItem = items.value.find(
      (cartItem) => String(cartItem.productId) === String(item.productId),
    )

    if (existingItem) {
      existingItem.quantity += item.quantity
      existingItem.totalPrice = existingItem.price * existingItem.quantity
    } else {
      items.value.push({
        ...item,
        price: item.price,
        totalPrice: item.price * item.quantity,
      })
    }

    recalculateTotals()
    saveToLocalStorage()
  }

  async function removeItem(productId: string | number) {
    if (shouldUseServerCart()) {
      const response = await cartApi.removeItem(productId)
      applyCartResponse(response)
      return
    }

    const index = items.value.findIndex((item) => String(item.productId) === String(productId))
    if (index === -1) return

    items.value.splice(index, 1)
    recalculateTotals()
    saveToLocalStorage()
  }

  async function updateQuantity(productId: string | number, quantity: number) {
    if (quantity <= 0) {
      await removeItem(productId)
      return
    }

    if (shouldUseServerCart()) {
      const response = await cartApi.updateItem(productId, quantity)
      applyCartResponse(response)
      return
    }

    const item = items.value.find((item) => String(item.productId) === String(productId))
    if (!item) return

    item.quantity = quantity
    item.totalPrice = item.price * quantity
    recalculateTotals()
    saveToLocalStorage()
  }

  async function clearCart() {
    if (shouldUseServerCart()) {
      const response = await cartApi.clearCart()
      applyCartResponse(response)
      return
    }

    items.value = []
    supplier.value = null
    totals.value = { totalAmount: 0, taxAmount: 0 }
    saveToLocalStorage()
  }

  /**
   * Замена корзины черновиком; при частичном сбое откат, inconsistent = неизвестно
   */
  async function restoreItems(restored_items: CartItem[]): Promise<CartRestoreResult> {
    const snapshot = items.value.map((item) => ({ ...item }))
    const snapshot_supplier = supplier.value === null ? null : { ...supplier.value }
    const failed_items: CartItem[] = []

    await clearCart()
    for (const item of restored_items) {
      try {
        await addItem(item)
      } catch {
        failed_items.push(item)
      }
    }

    if (failed_items.length === 0) {
      return { outcome: 'replaced', failedItems: [] }
    }

    const rolled_back = await rollbackToSnapshot(snapshot, snapshot_supplier)

    return {
      outcome: rolled_back ? 'rolled_back' : 'inconsistent',
      failedItems: failed_items,
    }
  }

  /**
   * Возврат корзины к снимку после неудачного восстановления черновика.
   *
   * @returns удалось ли вернуть прежнее содержимое
   */
  async function rollbackToSnapshot(
    snapshot: CartItem[],
    snapshot_supplier: { id: number; name: string } | null,
  ): Promise<boolean> {
    // Гостевая корзина живёт только в памяти и localStorage, сети между шагами нет -
    // откат делается присваиванием и провалиться не может.
    if (!shouldUseServerCart()) {
      items.value = snapshot.map((item) => ({ ...item }))
      supplier.value = snapshot_supplier
      recalculateTotals()
      saveToLocalStorage()

      return true
    }

    try {
      await clearCart()
      for (const item of snapshot) {
        await addItem(item)
      }

      return true
    } catch (e) {
      console.error('Не удалось вернуть корзину к исходному состоянию:', e)
      // Локальное состояние здесь обнулять нельзя: серверная корзина могла сохранить часть
      // позиций, и пустой список на клиенте разошёлся бы с тем, что реально лежит в заказе.
      // Перечитываем сервер - он источник правды, каким бы ни было содержимое.
      await resyncFromServer()

      return false
    }
  }

  /** Приводит клиентское состояние к серверному после сбоя, не догадываясь о содержимом. */
  async function resyncFromServer(): Promise<void> {
    try {
      applyCartResponse(await cartApi.getCart())
    } catch (e) {
      // Сервер недоступен и его состояние неизвестно. Показывать прежний список нельзя -
      // он заведомо не соответствует серверу; чистим и полагаемся на следующий успешный
      // запрос, а пользователю выше сообщается, что корзину надо проверить.
      console.error('Не удалось перечитать корзину с сервера:', e)
      items.value = []
      supplier.value = null
      totals.value = { totalAmount: 0, taxAmount: 0 }
      saveToLocalStorage()
    }
  }

  function getItem(productId: string | number): CartItem | undefined {
    return items.value.find((item) => String(item.productId) === String(productId))
  }

  function isInCart(productId: string | number): boolean {
    return items.value.some((item) => String(item.productId) === String(productId))
  }

  // Сохранение в localStorage
  function saveToLocalStorage() {
    try {
      localStorage.setItem('cart_items', JSON.stringify(items.value))
    } catch (e) {
      console.error('Ошибка сохранения корзины:', e)
    }
  }

  // Загрузка из localStorage
  function loadFromLocalStorage(): CartItem[] {
    try {
      const saved = localStorage.getItem('cart_items')
      if (!saved) return []

      const parsed = JSON.parse(saved)
      if (!Array.isArray(parsed)) return []

      return parsed
        .map((raw) => {
          if (!raw || typeof raw !== 'object') return null
          const quantity = Number(raw.quantity ?? 0)
          const rawPrice = Number(raw.price ?? 0)
          const hasTotal = typeof raw.totalPrice === 'number'
          const price = rawPrice
          const totalPrice = hasTotal ? Number(raw.totalPrice) : price * quantity

          return {
            ...raw,
            price,
            totalPrice,
            quantity,
          } as CartItem
        })
        .filter((item): item is CartItem => item !== null)
    } catch (e) {
      console.error('Ошибка загрузки корзины:', e)
      return []
    }
  }

  // Инициализация корзины
  async function initCart(): Promise<CartInitResult> {
    const emptyResult: CartInitResult = {
      syncedCount: 0,
      failedItems: [],
      warnings: [],
    }

    if (shouldUseServerCart()) {
      const localItems = loadFromLocalStorage()
      try {
        await fetchCart()
      } catch {
        items.value = localItems
        recalculateTotals()
        return emptyResult
      }

      if (localItems.length > 0) {
        const syncResult = await syncLocalItems(localItems)

        if (syncResult.failedItems.length === 0) {
          localStorage.removeItem('cart_items')
        } else {
          localStorage.setItem('cart_items', JSON.stringify(syncResult.failedItems))
        }

        return syncResult
      }

      return emptyResult
    }

    items.value = loadFromLocalStorage()
    recalculateTotals()
    return emptyResult
  }

  function shouldUseServerCart(): boolean {
    return authSession.isAuthenticated.value
  }

  function recalculateTotals() {
    const total = items.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const tax = items.value.reduce((sum, item) => sum + (item.taxAmount ?? 0) * item.quantity, 0)
    totals.value = {
      totalAmount: total,
      taxAmount: tax,
    }
  }

  function applyCartResponse(response: CartResponse) {
    const parseInteger = (value: string | number | undefined, defaultValue = 0): number => {
      if (typeof value === 'number') {
        return Math.floor(value)
      }
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10)
        return isNaN(parsed) ? defaultValue : parsed
      }
      return defaultValue
    }

    // Используем parsePrice для обработки объектов Money и преобразования в рубли
    items.value = response.items.map((item) => ({
      productId: item.product.id,
      name: item.product.name,
      ...(item.product.sku !== null ? { sku: item.product.sku } : {}),
      price: parsePrice(item.unit_price), // Преобразует Money в рубли
      priceWithTax: parsePrice(item.unit_price_with_tax),
      taxRate: parsePrice(item.tax_rate),
      taxAmount: parsePrice(item.tax_amount),
      totalPrice: parsePrice(item.total_price),
      quantity: parseInteger(item.quantity, 0),
      minimumOrder: parseInteger(item.minimum_order, 1),
      image: '',
    }))
    supplier.value = response.supplier
    totals.value = {
      totalAmount: parsePrice(response.total_amount), // Преобразует Money в рубли
      taxAmount: parsePrice(response.tax_amount),
    }
  }

  async function fetchCart() {
    loading.value = true
    try {
      const response = await cartApi.getCart()
      applyCartResponse(response)
    } catch (error) {
      items.value = []
      supplier.value = null
      totals.value = { totalAmount: 0, taxAmount: 0 }
      throw error
    } finally {
      loading.value = false
    }
  }

  async function syncLocalItems(localItems: CartItem[]): Promise<CartInitResult> {
    loading.value = true
    const failedItems: CartItem[] = []
    const warnings: string[] = []
    let syncedCount = 0

    try {
      for (const item of localItems) {
        try {
          await cartApi.addItem(item.productId, item.quantity)
          syncedCount += 1
        } catch (error) {
          failedItems.push(item)
          const message =
            error instanceof Error ? error.message : 'Не удалось перенести товар в корзину'
          warnings.push(message)
        }
      }

      await fetchCart()

      return {
        syncedCount,
        failedItems,
        warnings,
      }
    } finally {
      loading.value = false
    }
  }

  return {
    // Состояние
    items,
    loading,
    supplier,

    // Геттеры
    itemsCount,
    totalAmount,
    taxAmount,
    isEmpty,

    // Действия
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    restoreItems,
    getItem,
    isInCart,
    initCart,
  }
})
